import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CalendarBlank,
  User as UserIcon,
  CircleNotch,
  ArrowRight,
} from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/posts/${slug}`)
      .then((r) => setPost(r.data.post))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (post?.title) {
      const original = document.title;
      document.title = `${post.title} — Mine AI Journal`;
      return () => {
        document.title = original;
      };
    }
  }, [post]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 flex items-center gap-3 text-[#4A5568]">
        <CircleNotch className="animate-spin" weight="bold" /> Loading...
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20" data-testid="blog-not-found">
        <h1 className="font-display text-4xl text-[#0A1128]">Article not found</h1>
        <p className="mt-3 text-[#4A5568]">It may have been unpublished or moved.</p>
        <Link
          to="/blog"
          className="mt-6 inline-flex items-center gap-2 text-[#3B5BDB] hover:gap-3 smooth"
        >
          <ArrowLeft size={16} /> Back to Journal
        </Link>
      </div>
    );
  }
  if (!post) return null;

  return (
    <article data-testid="blog-post-page">
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-[#4A5568] hover:text-[#3B5BDB] inline-flex items-center gap-2 smooth"
          data-testid="blog-back"
        >
          <ArrowLeft size={14} /> Back
        </button>

        {post.tags?.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-1.5">
            {post.tags.map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#3B5BDB]/5 text-[#3B5BDB]"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 text-[#0A1128] tracking-tight leading-[1.05]">
          {post.title}
        </h1>

        <div className="mt-5 flex items-center gap-5 text-xs text-[#4A5568]">
          {post.author_name && (
            <span className="flex items-center gap-1.5">
              <UserIcon size={14} weight="duotone" /> {post.author_name}
            </span>
          )}
          {post.published_at && (
            <span className="flex items-center gap-1.5">
              <CalendarBlank size={14} weight="duotone" />
              {new Date(post.published_at).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </span>
          )}
        </div>

        {post.cover_image_url && (
          <img
            src={post.cover_image_url}
            alt=""
            className="mt-8 w-full aspect-[16/9] object-cover rounded-2xl border border-[#E8DFD1]"
            data-testid="blog-cover"
          />
        )}

        <div
          className="mt-10 prose-mineai"
          data-testid="blog-content"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>

        <div className="mt-16 pt-10 border-t border-[#E8DFD1] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="font-display text-2xl text-[#0A1128]">
            Question about gold or jewellery?
          </p>
          <Link
            to="/chat"
            className="px-5 py-2.5 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth inline-flex items-center gap-2"
            data-testid="blog-cta-chat"
          >
            Ask Mine AI <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </section>
    </article>
  );
}
